import { onlineManager, useIsFetching } from '@tanstack/react-query'
import { useEffect, useState, useSyncExternalStore } from 'react'
import type { CSSProperties } from 'react'
import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils.ts'
import { getTopBannerOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

const SHOW_DELAY_MS = 400

function useIsOnline(): boolean {
  return useSyncExternalStore(
    cb => onlineManager.subscribe(cb),
    () => onlineManager.isOnline(),
    () => true,
  )
}

export function useIsBackgroundSyncing(): boolean {
  const fetchingCount = useIsFetching()
  const isOnline = useIsOnline()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Offline means nothing can actually be syncing; suppress the banner
    // even if a query is mid-flight (request in limbo until timeout).
    if (!isOnline || fetchingCount === 0) {
      setVisible(false)
      return
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [fetchingCount, isOnline])

  return visible
}

type Props = {
  visible: boolean
  bannerIndex?: number
}

export default function BackgroundSyncBanner({ visible, bannerIndex = 0 }: Props) {
  const { t } = useTranslation()

  if (!visible) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed left-0 right-0 z-[110]',
        'top-[calc(var(--safe-area-inset-top)+var(--top-banner-offset,0px))]',
        'md:top-[var(--top-banner-offset,0px)]',
        'md:left-[var(--sidebar-width)]',
      )}
      style={{
        '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
      } as CSSProperties}
    >
      <div className="flex h-8 items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 dark:border-slate-800 dark:bg-slate-900/60">
        <RefreshCw className="size-3.5 shrink-0 animate-spin text-slate-500 dark:text-slate-400" />
        <span className="truncate text-xs text-slate-700 dark:text-slate-300">
          {t('sync.backgroundRefreshing')}
        </span>
      </div>
    </div>
  )
}
